import { useEffect, useState } from "react";

import { DataTable } from "../../components/DataTable";

import { Tag, CreateTagRequest } from "../../interfaces/Tags";

import { TagService } from "../../services/TagService";

export function CreateTags() {

    // =========================================
    // STATES
    // =========================================

    const [tags, setTags] =
        useState<Tag[]>([]);

    const [tagName, setTagName] =
        useState("");

    const [metaTitle, setMetaTitle] =
        useState("");

    const [metaDescription, setMetaDescription] =
        useState("");

    const [metaKeywords, setMetaKeywords] =
        useState("");

    const [canonicalUrl, setCanonicalUrl] =
        useState("");

    const [ogTitle, setOgTitle] =
        useState("");

    const [ogDescription, setOgDescription] =
        useState("");

    const [schemaMarkup, setSchemaMarkup] =
        useState("");

    const [editingTagId, setEditingTagId] =
        useState<number | null>(null);

    const [viewTag, setViewTag] =
        useState<Tag | null>(null);

    const [deleteConfirmId, setDeleteConfirmId] =
        useState<number | null>(null);

    // =========================================
    // LOAD TAGS
    // =========================================

    async function loadTags() {

        try {

            const data =
                await TagService.getAll();

            const sortedData =
                [...data].sort(
                    (a, b) =>
                        b.tagID - a.tagID
                );

            setTags(sortedData);

        } catch (error) {

            console.error(error);
        }
    }

    useEffect(() => {

        loadTags();

    }, []);

    // =========================================
    // RESET FORM
    // =========================================

    function resetForm() {

        setTagName("");

        setMetaTitle("");

        setMetaDescription("");

        setMetaKeywords("");

        setCanonicalUrl("");

        setOgTitle("");

        setOgDescription("");

        setSchemaMarkup("");

        setEditingTagId(null);
    }

    // =========================================
    // SUBMIT
    // =========================================

    async function handleSubmit() {

        try {

            const payload: CreateTagRequest = {

                tagName,

                metaTitle,

                metaDescription,

                metaKeywords,

                canonicalUrl,

                ogTitle,

                ogDescription,

                schemaMarkup
            };

            let response;

            // =====================================
            // UPDATE
            // =====================================

            if (editingTagId !== null) {

                response =
                    await TagService.update({

                        tagID:
                            editingTagId,

                        ...payload
                    });

            } else {

                // =====================================
                // CREATE
                // =====================================

                response =
                    await TagService.create(
                        payload
                    );
            }

            alert(response.message);

            await loadTags();

            resetForm();

        } catch (error: any) {

            console.error(error);

            alert(
                error.message ||
                "Error saving tag"
            );
        }
    }

    // =========================================
    // DELETE
    // =========================================

    async function deleteTag() {

        if (!deleteConfirmId) {
            return;
        }

        try {

            const response =
                await TagService.delete(
                    deleteConfirmId
                );

            alert(response.message);

            await loadTags();

            setDeleteConfirmId(null);

        } catch (error) {

            console.error(error);

            alert("Error deleting tag");
        }
    }

    // =========================================
    // EDIT
    // =========================================

    function editTag(item: Tag) {

        setEditingTagId(item.tagID);

        setTagName(item.tagName);

        setMetaTitle(item.metaTitle || "");

        setMetaDescription(item.metaDescription || "");

        setMetaKeywords(item.metaKeywords || "");

        setCanonicalUrl(item.canonicalUrl || "");

        setOgTitle(item.ogTitle || "");

        setOgDescription(item.ogDescription || "");

        setSchemaMarkup(item.schemaMarkup || "");

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }

    return (

        <div className="d-grid gap-4">

            {/* ========================================= */}
            {/* FORM */}
            {/* ========================================= */}

            <div className="card border-0 shadow-sm mst-card">

                <div className="card-body p-4">

                    <div className="d-flex align-items-center gap-2 mb-4">

                        <div className="mst-icon-box">
                            <i className="bi bi-tags"></i>
                        </div>

                        <div>

                            <h5 className="mb-0 fw-bold mst-title">
                                Create Tag
                            </h5>

                            <small className="mst-subtitle">
                                Add Tag
                            </small>

                        </div>
                    </div>

                    <div className="row g-3">

                        {/* TAG NAME */}

                        <div className="col-md-12">

                            <label className="form-label fw-semibold">
                                Tag Name
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                placeholder="Tag Name"
                                value={tagName}
                                onChange={(e) =>
                                    setTagName(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        {/* SEO SECTION */}

                        <div className="col-12 mt-4">

                            <h5>
                                SEO Settings
                            </h5>

                        </div>

                        <div className="col-md-6">

                            <label className="form-label">
                                Meta Title
                            </label>

                            <input
                                className="form-control"
                                value={metaTitle}
                                onChange={(e) =>
                                    setMetaTitle(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-6">

                            <label className="form-label">
                                Meta Keywords
                            </label>

                            <input
                                className="form-control"
                                value={metaKeywords}
                                onChange={(e) =>
                                    setMetaKeywords(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-12">

                            <label className="form-label">
                                Meta Description
                            </label>

                            <textarea
                                className="form-control"
                                rows={2}
                                value={metaDescription}
                                onChange={(e) =>
                                    setMetaDescription(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-6">

                            <label className="form-label">
                                Canonical URL
                            </label>

                            <input
                                className="form-control"
                                value={canonicalUrl}
                                onChange={(e) =>
                                    setCanonicalUrl(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-6">

                            <label className="form-label">
                                OG Title
                            </label>

                            <input
                                className="form-control"
                                value={ogTitle}
                                onChange={(e) =>
                                    setOgTitle(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-12">

                            <label className="form-label">
                                OG Description
                            </label>

                            <textarea
                                className="form-control"
                                rows={2}
                                value={ogDescription}
                                onChange={(e) =>
                                    setOgDescription(
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div className="col-md-12">

                            <label className="form-label">
                                Schema Markup
                            </label>

                            <textarea
                                className="form-control"
                                rows={4}
                                value={schemaMarkup}
                                onChange={(e) =>
                                    setSchemaMarkup(
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                    </div>

                    {/* BUTTONS */}

                    <div className="d-flex gap-2 mt-4">

                        <button
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={!tagName.trim()}
                        >
                            {editingTagId
                                ? "Update Tag"
                                : "Save Tag"}
                        </button>

                        <button
                            className="btn btn-secondary"
                            onClick={resetForm}
                        >
                            Reset
                        </button>

                    </div>
                </div>
            </div>

            {/* ========================================= */}
            {/* TABLE */}
            {/* ========================================= */}

            <div className="card border-0 shadow-sm">

                <div className="card-body">

                    <DataTable<Tag>

                        title="Tags"

                        rows={tags}

                        rowKey={(r) =>
                            r.tagID.toString()
                        }

                        searchPlaceholder="Search..."

                        exportFileName="tags"

                        columns={[

                            {
                                key: "tagID",
                                label: "ID"
                            },

                            {
                                key: "tagName",
                                label: "Tag Name"
                            },

                            {
                                key: "createdDate",
                                label: "Created",
                                render: (r) =>
                                    r.createdDate
                                        ? new Date(r.createdDate)
                                            .toLocaleDateString("en-GB")
                                        : "-"
                            },

                            {
                                key: "actions",
                                label: "Actions",

                                render: (r) => (

                                    <div className="d-flex gap-2">

                                        <button
                                            className="btn btn-info btn-sm"
                                            onClick={() => setViewTag(r)}
                                        >
                                            <i className="bi bi-eye"></i>
                                        </button>

                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => editTag(r)}
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                        </button>

                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() =>
                                                setDeleteConfirmId(
                                                    r.tagID
                                                )
                                            }
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>

                                    </div>
                                )
                            }

                        ]}
                    />
                </div>
            </div>


            {/* ========================================= */}
            {/* VIEW MODAL */}
            {/* ========================================= */}

            {viewTag && (

                <div
                    className="ns-overlay"
                    onClick={(e) => {

                        if (e.target === e.currentTarget) {

                            setViewTag(null);
                        }
                    }}
                >

                    <div className="ns-modal">

                        {/* HEADER */}

                        <div className="ns-modal-head">

                            <span className="ns-modal-title">
                                Tag Details
                                {" "}

                                <span
                                    className="badge bg-primary ms-2"
                                    style={{
                                        fontSize: "12px"
                                    }}
                                >
                                    ID: {viewTag.tagID}
                                </span>

                            </span>

                            <button
                                className="ns-modal-close"
                                onClick={() =>
                                    setViewTag(null)
                                }
                            >
                                ✕
                            </button>

                        </div>

                        {/* BODY */}

                        <div className="ns-modal-body">

                            <div className="row g-3">

                                <div className="col-md-12">

                                    <strong>Tag Name</strong>

                                    <div>
                                        {viewTag.tagName}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>Meta Title</strong>

                                    <div>
                                        {viewTag.metaTitle || "-"}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>Meta Keywords</strong>

                                    <div>
                                        {viewTag.metaKeywords || "-"}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>Meta Description</strong>

                                    <div>
                                        {viewTag.metaDescription || "-"}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>Canonical URL</strong>

                                    <div>
                                        {viewTag.canonicalUrl || "-"}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>OG Title</strong>

                                    <div>
                                        {viewTag.ogTitle || "-"}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>OG Description</strong>

                                    <div>
                                        {viewTag.ogDescription || "-"}
                                    </div>

                                </div>

                                <div className="col-md-12">

                                    <strong>Schema Markup</strong>

                                    <pre
                                        className="bg-light p-3 rounded mt-2"
                                        style={{
                                            whiteSpace: "pre-wrap",
                                            fontSize: "13px"
                                        }}
                                    >
                                        {viewTag.schemaMarkup || "-"}
                                    </pre>

                                </div>

                            </div>

                        </div>

                        {/* FOOTER */}

                        <div className="ns-modal-foot">

                            <button
                                className="ns-btn-sec"
                                onClick={() =>
                                    setViewTag(null)
                                }
                            >
                                Close
                            </button>

                        </div>

                    </div>

                </div>
            )}


            {/* ========================================= */}
            {/* DELETE CONFIRM MODAL */}
            {/* ========================================= */}

            {deleteConfirmId && (

                <div
                    className="pm-del-overlay"
                    onClick={(e) => {

                        if (e.target === e.currentTarget) {

                            setDeleteConfirmId(null);
                        }
                    }}
                >

                    <div className="pm-del-box">

                        <div className="pm-del-ico">

                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="2.5"
                            >
                                <polyline points="3 6 5 6 21 6" />

                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />

                                <path d="M10 11v6" />

                                <path d="M14 11v6" />
                            </svg>

                        </div>

                        <div className="pm-del-title">
                            Delete Tag?
                        </div>

                        <div className="pm-del-sub">
                            This action cannot be undone.
                            The tag will be permanently removed.
                        </div>

                        <div className="pm-del-btns">

                            <button
                                className="pm-btn-ghost"
                                onClick={() =>
                                    setDeleteConfirmId(null)
                                }
                                type="button"
                            >
                                Cancel
                            </button>

                            <button
                                className="pm-btn-danger"
                                onClick={deleteTag}
                                type="button"
                            >
                                Delete
                            </button>

                        </div>

                    </div>

                </div>
            )}
        </div>
    );
}